/**
 * The one way the frontend talks to the API.
 *
 * Nothing in the application calls `fetch` directly. Everything goes through
 * `apiGet` / `apiPost` / `apiPatch` / `apiDelete`, which:
 *
 *   - unwrap the Section 5.9 envelope, so callers receive `data`, not the wrapper;
 *   - turn any `{success:false}` body into a thrown `ApiRequestError` carrying the
 *     `code` from the shared package, so a screen can branch on
 *     `error.code === 'OVERLAP_DETECTED'` instead of matching on message text
 *     (Plan D4's "Continue Anyway" flow depends on exactly this);
 *   - attach the development identity header (Plan D10).
 *
 * Section 6 Principle: React holds no business logic. This file transports data
 * and nothing else.
 */

import { API_ERROR_CODE } from '@parinaam-vms/shared';
import type { ApiErrorCode, ApiResponse, Pagination } from '@parinaam-vms/shared';

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/api/v1';

/**
 * Development identity (Plan D10). Parinaam's identity provider replaces this
 * with a real credential; until then it lets a developer act as a named user so
 * audit rows are legible during a demo.
 */
const ACTOR_HEADER = 'X-Actor-Id';

let actorId: string | null = null;

export function setActorId(id: string | null): void {
  actorId = id;
}

/**
 * The bearer token for the signed-in account (D21).
 *
 * Module-level rather than passed per call: every request carries it, and
 * threading it through forty call sites is the churn the actor shim already
 * avoided once. `services/api/auth.ts` owns setting it.
 */
let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

/**
 * What to do when the API says the session is gone.
 *
 * The client cannot navigate on its own - it has no router - so the app
 * registers a handler here. Without one, a 401 in the middle of a task leaves
 * every panel showing "please sign in" with no way to do so.
 */
let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

/** A failed request, with the machine-readable code the UI branches on. */
export class ApiRequestError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: ApiErrorCode, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** True when the API rejected the *data*, so the form should show field errors. */
  get isValidation(): boolean {
    return this.code === API_ERROR_CODE.VALIDATION_ERROR;
  }

  /** True when the API is asking staff to confirm (Plan D3, D4). */
  get needsConfirmation(): boolean {
    return (
      this.code === API_ERROR_CODE.OVERLAP_DETECTED ||
      this.code === API_ERROR_CODE.ASSIGNMENT_REQUIRED
    );
  }
}

export interface Page<T> {
  items: T[];
  pagination?: Pagination;
}

type QueryScalar = string | number | boolean | null | undefined;
type QueryValue = QueryScalar | string[];

/**
 * Build a query string, dropping anything unset so `?status=` never appears.
 *
 * An array becomes a REPEATED parameter — `?program_id=a&program_id=b` — not a
 * comma-joined one. `set` would stringify the array to "a,b" and the API would
 * reject it as a malformed UUID; repeating the key is what Express parses back
 * into an array, and it needs no escaping rule of its own.
 *
 * An empty array is dropped like any other unset value. An empty filter means
 * "do not filter on this", and sending `?program_id=` would make it mean
 * "match nothing".
 *
 * Declare the argument type as a `type` alias rather than an `interface`. Only an
 * alias gets an implicit index signature, so an interface — however identical it
 * looks — is rejected here with "index signature is missing".
 */
export function toQueryString(params: Record<string, QueryValue> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const one of value) if (one !== '') search.append(key, one);
      continue;
    }
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

async function request<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (actorId) headers[ACTOR_HEADER] = actorId;
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // The API is not running, or the network is down. Distinct from an API that
    // answered with an error, and the message has to say so - Section 12.28 wants
    // the common local failure to explain itself.
    throw new ApiRequestError(
      API_ERROR_CODE.SERVICE_UNAVAILABLE,
      'Could not reach the API. Is the backend running?',
      0
    );
  }

  // 204, or a proxy returning HTML. Either way there is no envelope to read.
  const text = await response.text();
  if (!text) {
    if (response.ok) return { success: true, data: undefined as T };
    throw new ApiRequestError(
      API_ERROR_CODE.INTERNAL_ERROR,
      `The API returned ${response.status} with an empty body.`,
      response.status
    );
  }

  let payload: ApiResponse<T>;
  try {
    payload = JSON.parse(text) as ApiResponse<T>;
  } catch {
    throw new ApiRequestError(
      API_ERROR_CODE.INTERNAL_ERROR,
      'The API returned a response that was not JSON.',
      response.status
    );
  }

  if (!payload.success) {
    // A dead session, mid-task. Told to the app once, here, so every screen
    // does not have to recognise it - and only for 401: a 403 means the account
    // is real and simply may not do this, which is not a reason to sign anybody
    // out.
    if (response.status === 401 && !path.startsWith('/auth/')) {
      onUnauthenticated?.();
    }
    throw new ApiRequestError(
      payload.error.code,
      payload.error.message,
      response.status,
      payload.error.details
    );
  }

  return payload;
}

export async function apiGet<T>(path: string): Promise<T> {
  const payload = await request<T>('GET', path);
  return (payload as { data: T }).data;
}

/** A list endpoint: returns the rows and the pagination block together. */
export async function apiGetPage<T>(path: string): Promise<Page<T>> {
  const payload = await request<T[]>('GET', path);
  const success = payload as { data: T[]; pagination?: Pagination };
  return { items: success.data, pagination: success.pagination };
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const payload = await request<T>('POST', path, body ?? {});
  return (payload as { data: T }).data;
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const payload = await request<T>('PATCH', path, body);
  return (payload as { data: T }).data;
}

/**
 * For a sub-resource holding exactly one value, where the request replaces it
 * rather than merging into it — naming a session’s representative, or clearing
 * the name. PATCH would imply the absent fields are left alone, and there are
 * none.
 */
export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const payload = await request<T>('PUT', path, body);
  return (payload as { data: T }).data;
}

/**
 * `body` is optional and used by exactly one caller: deleting a volunteer needs
 * a reason (Plan D12). A DELETE carrying a body is unusual but legal, and the
 * alternative — a `POST /:id/delete` — would make the one destructive operation
 * in the system look like every other action.
 */
export async function apiDelete<T>(path: string, body?: unknown): Promise<T> {
  const payload = await request<T>('DELETE', path, body);
  return (payload as { data: T }).data;
}

/**
 * Download a file the API generates, and hand it to the browser.
 *
 * A plain `<a href>` cannot be used, and that is the whole reason this exists:
 * the export routes are behind `Authorization: Bearer`, and a link element
 * sends no headers. So the file is fetched like any other request, turned into
 * a blob, and given to a synthetic anchor.
 *
 * The filename comes from `Content-Disposition` when the server sent one — the
 * server knows what the file is called and stamps it with the date — and falls
 * back to the caller's suggestion only when the header is missing or a proxy
 * has stripped it.
 *
 * Errors still arrive as JSON, because a 403 is not a file. The envelope is
 * unwrapped the same way `request` does it, so a caller catches the same
 * `ApiRequestError` it would from any other call.
 */
export async function apiDownload(path: string, fallbackFilename: string): Promise<void> {
  const headers: Record<string, string> = {};
  if (actorId) headers[ACTOR_HEADER] = actorId;
  if (authToken) headers.Authorization = `Bearer ${authToken}`;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, { method: 'GET', headers });
  } catch {
    throw new ApiRequestError(
      API_ERROR_CODE.SERVICE_UNAVAILABLE,
      'Could not reach the API. Is the backend running?',
      0
    );
  }

  if (!response.ok) {
    const text = await response.text();
    let message = `The download failed (${response.status}).`;
    let code: ApiErrorCode = API_ERROR_CODE.INTERNAL_ERROR;
    try {
      const envelope = JSON.parse(text) as { error?: { code?: ApiErrorCode; message?: string } };
      if (envelope.error?.message) message = envelope.error.message;
      if (envelope.error?.code) code = envelope.error.code;
    } catch {
      // A proxy answered with HTML, or nothing. The status-based message stands.
    }
    throw new ApiRequestError(code, message, response.status);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filenameFrom(response) ?? fallbackFilename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Freed on the next tick rather than immediately: revoking synchronously can
  // beat the click in Safari and download an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `attachment; filename="parinaam-volunteers-2026-09-08.csv"` -> the filename. */
function filenameFrom(response: Response): string | null {
  const header = response.headers.get('content-disposition');
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Fetch a file rather than an envelope.
 *
 * Used by exactly one endpoint — the certificate PDF — which is the documented
 * exception to §5.9 because a PDF is not JSON. It lives here anyway rather than
 * in the certificates service, so "nothing in the application calls `fetch`
 * directly" stays literally true and the auth header has one owner.
 *
 * A *failure* is still an envelope: the API's error handler answers in JSON even
 * on this route, so the error path parses it and throws the same
 * `ApiRequestError` every other call would, and a screen can go on branching on
 * `error.code`.
 */
export async function apiGetBlob(path: string): Promise<Blob> {
  return blobRequest(path, 'GET');
}

/**
 * A file built from what you send it — a certificate previewed from wording
 * that has not been saved anywhere, so there is no id to GET.
 */
export async function apiPostBlob(path: string, body: unknown): Promise<Blob> {
  return blobRequest(path, 'POST', body);
}

async function blobRequest(path: string, method: 'GET' | 'POST', body?: unknown): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (actorId) headers[ACTOR_HEADER] = actorId;
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiRequestError(
      API_ERROR_CODE.SERVICE_UNAVAILABLE,
      'Could not reach the API. Is the backend running?',
      0
    );
  }

  if (!response.ok) {
    const text = await response.text();
    try {
      const payload = JSON.parse(text) as ApiResponse<never>;
      if (!payload.success) {
        if (response.status === 401) onUnauthenticated?.();
        throw new ApiRequestError(
          payload.error.code,
          payload.error.message,
          response.status,
          payload.error.details
        );
      }
    } catch (error) {
      // Rethrow the error built just above; anything else means the body was
      // not the envelope, which is a proxy or a crash rather than a refusal.
      if (error instanceof ApiRequestError) throw error;
    }
    throw new ApiRequestError(
      API_ERROR_CODE.INTERNAL_ERROR,
      `The API returned ${response.status} for a file request.`,
      response.status
    );
  }

  return response.blob();
}
